import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  Spin,
  Form,
  Button,
  Tooltip,
  Typography,
  Table,
  Breadcrumb,
  Result,
} from "antd";
import { fetchCompanies } from "../../redux/actions/companyActions";
import { ContainerOutlined, BorderlessTableOutlined,DownloadOutlined } from "@ant-design/icons";
import get from "lodash/get";
import dayjs from "dayjs";

import { getPartSummaryColumns } from "../../constants/tableColumns/summaryReportsColumns/getPartSummaryColumns";
import { COMPANY_ADMIN_ROLE_ACCESS } from "../../constants";

import requireAuth from "../../hocs/requireAuth";

import { fetchPartSummaryReport } from "../../redux/actions/reportActions";
import { fetchPartSummaryReportPdf } from "../../redux/actions/downloadPdfAction";
import HeaderSelector from "../../components/reports/partSummaryReport/headerSelector";
import ExcelExportButton from "../../components/breakdownSetting/ExcelExportButton";
import { useExcelExport } from "../../hooks/useExcelExport";

const { Title } = Typography;

const PageBreadcrumb = () => {
  return (
    <Breadcrumb
      style={{ fontSize: "14px" }}
      items={[
        {
          title: <BorderlessTableOutlined style={{ fontSize: "16px" }} />,
        },
        {
          title: (
            <>
              <ContainerOutlined style={{ fontSize: "16px" }} />
              <span>Part</span>
            </>
          ),
        },
        {
          title: "Summary Report",
        },
      ]}
    />
  );
};

const initialState = {
  company: undefined,
  templateType: undefined,
  date: dayjs(),
};

const PartSummaryReport = () => {
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [partSummary, setPartSummary] = useState(undefined);
  const [showEmpty, setShowEmpty] = useState(false);
  const [filter, setFilters] = useState(initialState);
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { wrapperRef, onDownload, isReady } = useExcelExport(
    [partSummary, getPartSummaryColumns],
    `Part-Summary-${get(filter, "date").format("MM-YYYY")}`,
    `Part-Summary`
  );
  const setinitialCompany = (company) => {
    setFilters({
      ...filter,
      templateType: undefined,
      company,
    });

    setConfig({
      ...config,
      companies: [company],
      templates: get(company, "template_access_list"),
    });
    form.setFieldsValue({
      template: undefined,
      Company: get(company, "id"),
    });
  };

  const getCompanies = () => {
    setLoading(true);
    dispatch(fetchCompanies())
      .then((result) => {
        if (result.length === 1) {
          setinitialCompany(result[0]);
        } else {
          setConfig({ companies: result });
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const getSummary = () => {
    setLoading(true);
    const query = {
      type: get(filter, "templateType"),
      company_uuid: get(filter, "company.id"),
      date: get(filter, "date").format("MM-YYYY"),
    };
    dispatch(fetchPartSummaryReport(query))
      .then((result) => {
        if (get(result, "reports", []).length > 0) {
          setShowEmpty(false);
          setPartSummary(result);
        } else {
          setPartSummary(undefined);
          setShowEmpty(true);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const onDownloadReport = () => {
    setLoading(true);
    const query = {
      type: get(filter, "templateType"),
      company_uuid: get(filter, "company.id"),
      date: get(filter, "date").format("MM-YYYY"),
    };
    dispatch(fetchPartSummaryReportPdf(query))
      .then((result) => {
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getCompanies();
  }, []);

  return (
    <div className="p-2 h-full flex flex-col">
      <div className="flex flex-row justify-between pb-4">
        <PageBreadcrumb />
      </div>

      <div className="h-full rounded-xl overflow-hidden bg-white flex-1 overflow-y-auto scrollbar-hidden pb-5 min-w-full">
        <Form form={form}>
          <Spin spinning={loading}>
            <HeaderSelector
              form={form}
              filter={filter}
              config={config}
              setConfig={setConfig}
              setFilters={setFilters}
              getSummary={getSummary}
              setPartSummary={setPartSummary}
            />
            {get(partSummary, "reports", []).length > 0 && (
              <div className="w-full px-8 overflow-y-auto">
                <div className="float-right mb-5 space-x-2">
                  <Tooltip className="mb-5" title="Download Report">
                    <Button
                      type="primary"
                      icon={<DownloadOutlined />}
                      onClick={() => onDownloadReport()}
                    />
                  </Tooltip>
                  <Tooltip title="Excel Summary Report">
                    <ExcelExportButton
                      onDownload={onDownload}
                      disabled={!isReady}
                    />
                  </Tooltip>
                </div>

                <div className="w-full table-container" ref={wrapperRef}>
                  <Title className="mt-10" level={5}>
                    Part Summary
                  </Title>
                  <Table
                    className="mb-20 mt-10 custom-horizontal-scrollbar"
                    dataSource={get(partSummary, "reports", [])}
                    columns={getPartSummaryColumns(
                      get(partSummary, "weeks", []), get(filter, "templateType")
                    )}
                    bordered
                    pagination={false}
                   rowKey={(record) => get(record, "id") || crypto.randomUUID()}
                  />
                </div>
              </div>
            )}

            {showEmpty && (
              <div className="px-8 py-8">
                <Result
                  title={`No parts exists...for this month!`}
                  subTitle="Please try again with another company, template type or month"
                  status="404"
                />
              </div>
            )}
          </Spin>
        </Form>
      </div>
    </div>
  );
};

export default requireAuth(PartSummaryReport,COMPANY_ADMIN_ROLE_ACCESS);
